import React, { createContext, useState, useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { useQueryClient, useIsMutating } from '@tanstack/react-query';
import { 
  getMessages, 
  markMessageAsRead, 
  deleteMessage, 
  getAdminPendingProposalsCount, 
  saveWebPushSubscription,
  saveFcmDeviceToken,
  fetchAuthenticated,
  getPreferredPersonaggio,
  setPreferredPersonaggio,
  getPersonaggioDetail,
  getPersonaggioGameState,
  getAcquirableSkills,
  getAcquirableInfusioni,
  getAcquirableTessiture,
  getAcquirableCerimoniali,
  getCampaigns,
  validateActiveCampaign,
  getActiveCampaignSlug,
  setActiveCampaignSlug,
  normCampaignSlug,
  postEventoPremiApplica,
  getGiocoEventoStato,
} from '../api';
import NotificationPopup from './NotificationPopup';
import PriorityMessageOverlay from './PriorityMessageOverlay';
import { putOfflineGameStateSnapshot, putOfflineCharacterDetail } from '../lib/offlineGameStateDb';
import { putOfflineMessages, getOfflineMessages } from '../lib/offlineMessagesDb';
import { isWebPushSupported } from '../lib/webpush';
import { activateDevicePush, isDevicePushSupported } from '../lib/devicePush';
import { isNativeApp } from '../lib/nativePlatform';
import { ensureAppServiceWorker } from '../lib/appServiceWorker';
import { canAccessModuloMode, getModuloAccesso } from '../lib/campagnaModuli';
import { htmlToPlainText } from '../utils/htmlSanitizer';
import { mergeActiveTimers, normalizeTimerRow, timerStateKey } from '../utils/activeTimers';
import { GIOCO_EVENTO_STATO_VUOTO, mergeGiocoEventoStato } from '../utils/giocoEventoStato';

import { 
  usePunteggi, 
  useStatisticaContainers,
  usePersonaggiList, 
  usePersonaggioDetail, 
  useAcquirableSkills, 
  useAcquirableInfusioni, 
  useAcquirableTessiture,
  useAcquirableCerimoniali
} from '../hooks/useGameData';

export const CharacterContext = createContext(null);

// --- HELPER UTILS ---

const sendSystemNotification = (title, body, options = {}) => {
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    try {
      const n = new Notification(title, {
        body,
        icon: '/pwa-192x192.png',
        badge: '/pwa-192x192.png',
        tag: options.tag || undefined,
        renotify: Boolean(options.renotify),
        data: options.data || { url: '/?tab=messaggi' },
      });
      n.onclick = () => {
        try {
          window.focus();
          window.dispatchEvent(new CustomEvent('kor35:open-messaggi'));
        } catch {
          /* noop */
        }
        n.close();
      };
    } catch (e) {
      console.error("Errore notifica:", e);
    }
};

export const CharacterProvider = ({ children, onLogout }) => {
  const queryClient = useQueryClient();
  
  const mutatingCount = useIsMutating();
  
  // --- STATI GLOBALI UI ---
  const [selectedCharacterId, setSelectedCharacterId] = useState(() => localStorage.getItem('kor35_last_char_id') || '');
  const [preferredCharacterId, setPreferredCharacterId] = useState(() => localStorage.getItem('kor35_preferred_char_id') || '');
  const [preferredResolved, setPreferredResolved] = useState(false);
  /**
   * Solo superuser Django: bypass globale (es. notifiche sistema, vedi tutti i PG).
   * I permessi di gioco usano i ruoli di campagna (HEAD_MASTER, MASTER, …), non is_staff.
   */
  const [isGlobalSuperuser, setIsGlobalSuperuser] = useState(() => {
    const stored = localStorage.getItem('kor35_is_admin');
    if (stored !== null) return stored === 'true';
    return localStorage.getItem('kor35_is_master') === 'true';
  });
  const [isDjangoStaff, setIsDjangoStaff] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await fetchAuthenticated(
          '/api/personaggi/api/user/me/',
          { method: 'GET' },
          onLogout
        );
        if (cancelled || !me || typeof me !== 'object') return;
        const serverSuper = !!me.is_superuser;
        setIsGlobalSuperuser(serverSuper);
        localStorage.setItem('kor35_is_admin', String(serverSuper));
        setIsDjangoStaff(!!me.is_staff || serverSuper);
      } catch {
        /* mantieni fallback iniziale da localStorage */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onLogout]);
  const [staffWorkMode, setStaffWorkMode] = useState('dashboard');
  const [campaigns, setCampaigns] = useState([]);
  const [activeCampaign, setActiveCampaign] = useState(() => getActiveCampaignSlug());
  const activeCampaignMeta =
    campaigns.find((c) => normCampaignSlug(c.slug) === normCampaignSlug(activeCampaign)) || null;
  const activeCampaignRole = String(activeCampaignMeta?.ruolo || 'PLAYER').trim().toUpperCase();
  const isCampaignHeadMaster = activeCampaignRole === 'HEAD_MASTER';
  const isCampaignMaster =
    activeCampaignRole === 'MASTER' || activeCampaignRole === 'HEAD_MASTER';
  const isCampaignStaffer =
    activeCampaignRole === 'STAFFER' ||
    activeCampaignRole === 'MASTER' ||
    activeCampaignRole === 'HEAD_MASTER';
  const isCampaignHelper = activeCampaignRole === 'HELPER';
  const canSeeStaffCompiti = isCampaignStaffer || isCampaignHelper;
  const isCampaignRedactor = isCampaignStaffer || activeCampaignRole === 'REDACTOR';
  const canUseWizardTest = isGlobalSuperuser || isDjangoStaff || isCampaignStaffer;
  const moduliAccesso = useMemo(
    () => activeCampaignMeta?.moduli_accesso || {},
    [activeCampaignMeta],
  );

  const [viewAll, setViewAll] = useState(false);
  const [giocoEventoStato, setGiocoEventoStato] = useState(() => ({ ...GIOCO_EVENTO_STATO_VUOTO }));
  
  // --- NUOVO STATO PER I TIMER (GESTIONE GLOBALE) ---
  const [activeTimers, setActiveTimers] = useState({});

  const updateTimerState = useCallback((timerData) => {
    const normalized = normalizeTimerRow(timerData);
    if (!normalized) return;
    const key = timerStateKey(normalized);
    setActiveTimers(prev => ({
        ...prev,
        [key]: {
            ...normalized,
            variant: normalized.variant || prev[key]?.variant,
        }
    }));
  }, []);

  const removeTimerState = useCallback((nomeTimer) => {
    setActiveTimers(prev => {
        const newState = { ...prev };
        delete newState[nomeTimer];
        return newState;
    });
  }, []);

  // Rilegge i timer all'apertura, al cambio PG, al ritorno in foreground e al cambio pagina.
  // Il countdown poi corre in locale: niente sync di rete ogni secondo.
  const refreshActiveTimers = useCallback(async () => {
    try {
      const qs = selectedCharacterId ? `?personaggio_id=${selectedCharacterId}` : '';
      const data = await fetchAuthenticated(`/api/personaggi/api/timers/active/${qs}`, onLogout);
      if (Array.isArray(data)) {
        setActiveTimers((prev) => mergeActiveTimers(prev, data));
      }
    } catch (err) {
      console.error('Errore caricamento timer', err);
    }
  }, [onLogout, selectedCharacterId]);

  useEffect(() => {
    refreshActiveTimers();
  }, [refreshActiveTimers]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshActiveTimers();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshActiveTimers]);

  const refreshCampaigns = useCallback(async () => {
    try {
      const list = await getCampaigns(onLogout);
      setCampaigns(Array.isArray(list) ? list : []);
      return Array.isArray(list) ? list : [];
    } catch {
      setCampaigns([]);
      return [];
    }
  }, [onLogout]);

  useEffect(() => {
    const syncCampaign = async () => {
      try {
        const valid = await validateActiveCampaign(activeCampaign, onLogout);
        const normalized = setActiveCampaignSlug(valid?.slug || activeCampaign);
        setActiveCampaign(normalized);
        // Allinea moduli/ruolo senza aspettare un reload completo della lista.
        if (valid && typeof valid === 'object' && valid.slug) {
          setCampaigns((prev) => {
            const slug = normCampaignSlug(valid.slug);
            const idx = prev.findIndex((c) => normCampaignSlug(c.slug) === slug);
            if (idx < 0) return prev;
            const next = [...prev];
            next[idx] = {
              ...next[idx],
              ...valid,
              moduli_accesso: valid.moduli_accesso || next[idx].moduli_accesso || {},
            };
            return next;
          });
        }
      } catch {
        setActiveCampaign(setActiveCampaignSlug('kor35'));
      }
    };
    syncCampaign();
  }, [activeCampaign, onLogout]);

  useEffect(() => {
    refreshCampaigns();
  }, [refreshCampaigns, activeCampaign]);

  useEffect(() => {
    let cancelled = false;
    const loadGiocoEventoStato = async () => {
      try {
        const data = await getGiocoEventoStato(onLogout);
        if (!cancelled && data && typeof data === 'object') {
          setGiocoEventoStato((prev) => mergeGiocoEventoStato(prev, data));
        }
      } catch {
        /* rete: mantieni ultimo stato noto */
      }
    };
    loadGiocoEventoStato();
    const interval = setInterval(loadGiocoEventoStato, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [onLogout]);

  // --- REACT QUERY HOOKS ---
  
  // 1. Punteggi
  const { data: punteggiList = [], isLoading: isLoadingPunteggi } = usePunteggi(onLogout);

  // 1b. Contenitori statistiche (config scheda)
  const { data: statisticaContainers = [], isLoading: isLoadingStatContainers } = useStatisticaContainers(onLogout);

  // 2. Lista Personaggi
  const { 
    data: personaggiList = [], 
    isLoading: isLoadingList, 
    refetch: refetchPersonaggiList 
  } = usePersonaggiList(onLogout, viewAll);

  // 3. Dettaglio Personaggio Selezionato
  const { 
    data: selectedCharacterData, 
    isLoading: isLoadingDetail,
    refetch: refetchCharacterDetail
  } = usePersonaggioDetail(selectedCharacterId, onLogout);

  /**
   * PnG non giocante: come lato API, vede i moduli in TEST anche senza ruolo staff.
   * Il dettaglio è la fonte più affidabile; in fallback si usa la lista personaggi.
   */
  const isPngStaffAttivo = useMemo(() => {
    const flagGiocante = (row) => {
      if (!row || typeof row !== 'object') return null;
      if (typeof row.tipologia_giocante === 'boolean') return row.tipologia_giocante;
      if (typeof row.giocante === 'boolean') return row.giocante;
      if (typeof row.tipologia?.giocante === 'boolean') return row.tipologia.giocante;
      return null;
    };
    const fromDetail = flagGiocante(selectedCharacterData);
    if (fromDetail !== null) return !fromDetail;
    const row = Array.isArray(personaggiList)
      ? personaggiList.find((p) => String(p?.id) === String(selectedCharacterId))
      : null;
    const fromList = flagGiocante(row);
    return fromList === null ? false : !fromList;
  }, [selectedCharacterData, personaggiList, selectedCharacterId]);

  const canAccessModulo = useCallback((key, { isPngStaff } = {}) => {
    const modo = getModuloAccesso(moduliAccesso, key);
    return canAccessModuloMode(modo, {
      isCampaignStaffer,
      isDjangoStaff,
      isGlobalSuperuser,
      isPngStaff: isPngStaff === undefined ? isPngStaffAttivo : !!isPngStaff,
    });
  }, [moduliAccesso, isCampaignStaffer, isDjangoStaff, isGlobalSuperuser, isPngStaffAttivo]);

  /** Aggiorna IndexedDB (snapshot di gioco + dettaglio Scheda) dopo ogni sync online del personaggio. */
  useEffect(() => {
    if (!selectedCharacterId || typeof navigator === 'undefined' || !navigator.onLine) {
      return undefined;
    }
    if (!selectedCharacterData) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        await putOfflineCharacterDetail(selectedCharacterId, selectedCharacterData);
        const snap = await getPersonaggioGameState(selectedCharacterId, onLogout);
        if (!cancelled && snap) {
          await putOfflineGameStateSnapshot(selectedCharacterId, snap);
        }
      } catch {
        /* rete / auth: non bloccare la UI */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCharacterId, selectedCharacterData, onLogout]);

  /** Registra SW per precache shell (offline) anche senza push. */
  useEffect(() => {
    ensureAppServiceWorker();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await postEventoPremiApplica(onLogout);
        if (!cancelled) {
          await refetchPersonaggiList();
          if (selectedCharacterId) {
            await refetchCharacterDetail();
          }
          try {
            const stato = await getGiocoEventoStato(onLogout);
            if (stato && typeof stato === 'object') {
              setGiocoEventoStato((prev) => mergeGiocoEventoStato(prev, stato));
            }
          } catch {
            /* noop */
          }
        }
      } catch {
        /* Nessun premio applicabile o errore rete: non bloccare l'app */
      }
    })();
    return () => {
      cancelled = true;
    };
    // Eseguito al mount del provider (es. dopo login o refresh): idempotente lato server.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- evita re-run ad ogni cambio refetch RQ
  }, [onLogout]);

  // 4. Dati Lazy Loading (Estraiamo anche i refetch)
  const { 
      data: acquirableSkills = [], 
      refetch: refetchSkills 
  } = useAcquirableSkills(selectedCharacterId, onLogout);

  const { 
      data: acquirableInfusioni = [], 
      refetch: refetchInfusioni 
  } = useAcquirableInfusioni(selectedCharacterId, onLogout);

  const { 
      data: acquirableTessiture = [], 
      refetch: refetchTessiture 
  } = useAcquirableTessiture(selectedCharacterId, onLogout);

  const { 
      data: acquirableCerimoniali = [], 
      refetch: refetchCerimoniali 
  } = useAcquirableCerimoniali(selectedCharacterId, onLogout);

  const personaggiListOrdinata = useMemo(() => {
    const list = Array.isArray(personaggiList) ? [...personaggiList] : [];
    const pref = String(preferredCharacterId || '');
    list.sort((a, b) => {
      const ap = pref && String(a?.id) === pref ? 0 : 1;
      const bp = pref && String(b?.id) === pref ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return String(a?.nome || '').localeCompare(String(b?.nome || ''), 'it', { sensitivity: 'base' });
    });
    return list;
  }, [personaggiList, preferredCharacterId]);

  // --- LOGICA SELEZIONE AUTOMATICA PG ---
  // Il preferito è il default solo dopo la risposta del server: altrimenti la lista
  // vuota (o il primo PG) vincerebbe la corsa e il preferito non verrebbe riaperto.
  useEffect(() => {
    if (!preferredResolved) return;
    if (selectedCharacterId) return;
    if (!personaggiListOrdinata.length) return;
    const preferredId = preferredCharacterId || localStorage.getItem('kor35_preferred_char_id');
    const lastId = localStorage.getItem('kor35_last_char_id');
    const targetId = (preferredId && personaggiListOrdinata.some(p => p.id.toString() === String(preferredId)))
                     ? preferredId
                     : ((lastId && personaggiListOrdinata.some(p => p.id.toString() === String(lastId)))
                       ? lastId
                       : personaggiListOrdinata[0].id);
    setSelectedCharacterId(String(targetId));
  }, [personaggiListOrdinata, selectedCharacterId, preferredCharacterId, preferredResolved]);

  useEffect(() => {
    let mounted = true;
    const loadPreferredFromServer = async () => {
      try {
        const data = await getPreferredPersonaggio(onLogout);
        if (!mounted) return;
        const serverPreferred = data?.preferred_personaggio_id;
        if (serverPreferred) {
          const normalized = String(serverPreferred);
          setPreferredCharacterId(normalized);
          localStorage.setItem('kor35_preferred_char_id', normalized);
          const slug = data?.campagna_slug;
          const aperturaEsplicita = localStorage.getItem('kor35_last_char_id');
          if (
            !aperturaEsplicita &&
            slug &&
            normCampaignSlug(slug) !== normCampaignSlug(getActiveCampaignSlug())
          ) {
            try {
              const valid = await validateActiveCampaign(slug, onLogout);
              if (!mounted) return;
              const campagna = setActiveCampaignSlug(valid?.slug || slug);
              setActiveCampaign(campagna);
              await queryClient.invalidateQueries({ queryKey: ['personaggi_list'] });
            } catch {
              /* la campagna del preferito non è accessibile: resta quella corrente */
            }
          }
          return;
        }
        const localPreferred = localStorage.getItem('kor35_preferred_char_id');
        if (localPreferred) {
          setPreferredCharacterId(String(localPreferred));
          try {
            await setPreferredPersonaggio(Number(localPreferred), onLogout);
          } catch {
            /* il server non ha ancora la preferenza: resta sul dispositivo */
          }
        }
      } catch (err) {
        // fallback locale: nessun blocco UI
      } finally {
        if (mounted) setPreferredResolved(true);
      }
    };
    loadPreferredFromServer();
    return () => {
      mounted = false;
    };
  }, [onLogout, queryClient]);

  // --- AZIONI CONTEXT ---
  const handleSelectCharacter = useCallback(async (id) => {
    const normalizedId = id ? String(id) : '';
    if (!normalizedId) {
      setSelectedCharacterId('');
      localStorage.removeItem('kor35_last_char_id');
      return;
    }

    const target = personaggiList.find((p) => String(p.id) === normalizedId);
    const targetCampaignId = target?.campagna ? String(target.campagna) : null;
    const targetCampaign = targetCampaignId
      ? campaigns.find((c) => String(c.id) === targetCampaignId)
      : null;

    if (
      targetCampaign?.slug &&
      normCampaignSlug(targetCampaign.slug) !== normCampaignSlug(activeCampaign)
    ) {
      try {
        const previousCampaign = campaigns.find(
          (c) => normCampaignSlug(c.slug) === normCampaignSlug(activeCampaign)
        );
        await validateActiveCampaign(targetCampaign.slug, onLogout);
        setActiveCampaign(setActiveCampaignSlug(targetCampaign.slug));
        await queryClient.invalidateQueries();
        setNotification({
          titolo: 'Campagna aggiornata',
          testo: `Cambio automatico da <strong>${previousCampaign?.nome || activeCampaign}</strong> a <strong>${targetCampaign.nome}</strong> per aprire ${target?.nome || 'il personaggio'} in contesto corretto.`,
          mittente: 'Sistema',
        });
      } catch {
        // Se la campagna non è valida/accessibile, lasciamo la campagna corrente.
      }
    }

    setSelectedCharacterId(normalizedId);
    localStorage.setItem('kor35_last_char_id', normalizedId);
  }, [personaggiList, campaigns, activeCampaign, onLogout, queryClient]);

  const setPreferredCharacter = useCallback((id) => {
    const persist = async (value) => {
      try {
        await setPreferredPersonaggio(value || null, onLogout);
      } catch (err) {
        // fallback locale: l'utente mantiene la preferenza sul dispositivo corrente
      }
    };

    if (!id) {
      setPreferredCharacterId('');
      localStorage.removeItem('kor35_preferred_char_id');
      persist(null);
      return;
    }
    const normalized = String(id);
    setPreferredCharacterId(normalized);
    localStorage.setItem('kor35_preferred_char_id', normalized);
    persist(Number(normalized));
  }, [onLogout]);

  const changeActiveCampaign = useCallback(async (slug) => {
    const normalized = setActiveCampaignSlug(slug);
    await validateActiveCampaign(normalized, onLogout);
    setActiveCampaign(normalized);
    setSelectedCharacterId('');
    localStorage.removeItem('kor35_last_char_id');
    await queryClient.invalidateQueries();
  }, [onLogout, queryClient]);

  // *** CORREZIONE CRUCIALE ***
  // Invalidazione + refetch esplicito della query personaggio così la UI (timer creazioni
  // consumabili, pulsante "Aggiungi a inventario") si aggiorna subito senza ricaricare la pagina.
  const refreshCharacterData = useCallback(async () => {
    if (selectedCharacterId) {
      const cId = selectedCharacterId;
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ['personaggio', cId] }),
        queryClient.cancelQueries({ queryKey: ['abilita_acquistabili', cId] }),
        queryClient.cancelQueries({ queryKey: ['infusioni_acquistabili', cId] }),
        queryClient.cancelQueries({ queryKey: ['tessiture_acquistabili', cId] }),
        queryClient.cancelQueries({ queryKey: ['cerimoniali_acquistabili', cId] }),
      ]);

      // Soft sync: fetch reale backend e update cache senza svuotarla (evita flicker).
      const [personaggio, abilita, infusioni, tessiture, cerimoniali] = await Promise.all([
        getPersonaggioDetail(cId, onLogout),
        getAcquirableSkills(onLogout, cId),
        getAcquirableInfusioni(cId),
        getAcquirableTessiture(cId),
        getAcquirableCerimoniali(cId, onLogout),
      ]);

      queryClient.setQueryData(['personaggio', cId], personaggio);
      queryClient.setQueryData(['abilita_acquistabili', cId], abilita);
      queryClient.setQueryData(['infusioni_acquistabili', cId], infusioni);
      queryClient.setQueryData(['tessiture_acquistabili', cId], tessiture);
      queryClient.setQueryData(['cerimoniali_acquistabili', cId], cerimoniali);

      // Evita doppio roundtrip: i dati sono gia stati appena sincronizzati via fetch manuale.
      // Le mutation/hook invalidano comunque quando serve una riallineamento successivo.
    } else {
      await Promise.all([
        refetchSkills(),
        refetchInfusioni(),
        refetchTessiture(),
        refetchCerimoniali(),
        refetchCharacterDetail(),
      ]);
    }
  }, [selectedCharacterId, queryClient, refetchCharacterDetail, refetchSkills, refetchInfusioni, refetchTessiture, refetchCerimoniali]);

  // Tornando alla PWA / scheda (es. dopo modifiche dallo smartwatch) allinea i dati se il WS era in pausa.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible' || !selectedCharacterId) return;
      refreshCharacterData();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [selectedCharacterId, refreshCharacterData]);

  const fetchPersonaggi = useCallback(() => {
    return refetchPersonaggiList();
  }, [refetchPersonaggiList]);

  const toggleViewAll = () => setViewAll(prev => !prev);

  // --- GESTIONE MESSAGGI ---
  const [userMessages, setUserMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const toggleReadInProgress = useRef(new Set()); // Lock per prevenire chiamate duplicate

  const fetchUserMessages = useCallback(async (charId) => {
    if (!charId) return;
    try {
      const rawMsgs = await getMessages(charId, onLogout);
      const msgs = (rawMsgs || []).map(msg => ({
          ...msg,
          letto: msg.letto  // Usa direttamente il campo 'letto' dal serializer
      }));
      const sorted = (msgs || []).sort((a, b) => {
        if (a.letto !== b.letto) return a.letto ? 1 : -1;
        return new Date(b.data_invio) - new Date(a.data_invio);
      });
      setUserMessages(sorted);
      setUnreadCount(sorted.filter(m => !m.letto).length);
      if (typeof navigator === 'undefined' || navigator.onLine) {
        putOfflineMessages(charId, sorted).catch(() => {});
      }
    } catch (err) {
      console.error("Err msg:", err);
      try {
        const cached = await getOfflineMessages(charId);
        if (cached?.messages?.length) {
          setUserMessages(cached.messages);
          setUnreadCount(cached.messages.filter((m) => !m.letto).length);
        }
      } catch {
        /* ignore */
      }
    }
  }, [onLogout]);

  useEffect(() => {
    if (selectedCharacterId) fetchUserMessages(selectedCharacterId);
  }, [selectedCharacterId, fetchUserMessages]);

  const handleMarkAsRead = async (msgId) => {
      setUserMessages(prev => prev.map(m => m.id === msgId ? { ...m, letto: true } : m)); 
      setUnreadCount(prev => Math.max(0, prev - 1));
      try { await markMessageAsRead(msgId, selectedCharacterId, onLogout); } 
      catch (e) { fetchUserMessages(selectedCharacterId); }
  };

  const handleToggleRead = async (msgId) => {
      // Previeni chiamate duplicate
      if (toggleReadInProgress.current.has(msgId)) {
          console.log('Toggle già in corso per messaggio', msgId);
          return;
      }
      
      const msg = userMessages.find(m => m.id === msgId);
      if (!msg) return;
      
      // Aggiungi al set di operazioni in corso
      toggleReadInProgress.current.add(msgId);
      
      const newStatus = !msg.letto;
      console.log(`Toggle read per messaggio ${msgId}: ${msg.letto} -> ${newStatus}`);
      
      // Aggiorna ottimisticamente
      setUserMessages(prev => prev.map(m => m.id === msgId ? { ...m, letto: newStatus } : m)); 
      setUnreadCount(prev => newStatus ? Math.max(0, prev - 1) : prev + 1);
      
      try { 
          await fetchAuthenticated(`/api/personaggi/api/messaggi/${msgId}/toggle_letto/`, {
              method: 'POST',
              body: JSON.stringify({ personaggio_id: selectedCharacterId })
          }, onLogout);
          
          // Ricarica dal server DOPO un breve delay per permettere al DB di aggiornarsi
          setTimeout(async () => {
              console.log('Ricaricando messaggi dopo toggle...');
              await fetchUserMessages(selectedCharacterId);
              // Rimuovi dal set dopo il reload
              toggleReadInProgress.current.delete(msgId);
          }, 300);
      } 
      catch (e) { 
          console.error('Errore toggle read:', e);
          // In caso di errore, ricarica subito per ripristinare lo stato corretto
          fetchUserMessages(selectedCharacterId);
          toggleReadInProgress.current.delete(msgId);
      }
  };

  const handleDeleteMessage = async (msgId) => {
      if(!window.confirm("Cancellare messaggio?")) return;
      setUserMessages(prev => prev.filter(m => m.id !== msgId)); 
      try { await deleteMessage(msgId, selectedCharacterId, onLogout); } 
      catch (e) { fetchUserMessages(selectedCharacterId); }
  };

  // --- ADMIN & NOTIFICHE ---
  const [adminPendingCount, setAdminPendingCount] = useState(0);
  useEffect(() => {
      if (isGlobalSuperuser && !viewAll) {
          const check = async () => {
              try { const d = await getAdminPendingProposalsCount(onLogout); setAdminPendingCount(d.count); } 
              catch (e) {}
          };
          check();
          const i = setInterval(check, 60000);
          return () => clearInterval(i);
      }
  }, [isGlobalSuperuser, viewAll, onLogout]);

  const subscribeToPush = useCallback(async () => {
    const result = await activateDevicePush();
    if (!result.ok) return result;
    try {
      if (result.channel === 'fcm') {
        await saveFcmDeviceToken(
          { token: result.token, platform: result.platform || 'android' },
          onLogout
        );
        try {
          localStorage.setItem('kor35_fcm_ok', '1');
        } catch {
          /* ignore */
        }
      } else {
        await saveWebPushSubscription(result.subscription, onLogout);
      }
      return { ok: true, channel: result.channel };
    } catch (e) {
      console.error('Device push error:', e);
      return {
        ok: false,
        reason: 'error',
        message: e?.message || 'Errore nel salvataggio della sottoscrizione push.',
      };
    }
  }, [onLogout]);

  // Re-sottoscrive: in shell nativa sempre (FCM ha i propri permessi);
  // sul web solo se Notification.permission è già granted.
  useEffect(() => {
    if (!selectedCharacterId || !isDevicePushSupported()) return;
    if (!isNativeApp()) {
      if (!isWebPushSupported() || typeof Notification === 'undefined') return;
      if (Notification.permission !== 'granted') return;
    }
    subscribeToPush();
  }, [selectedCharacterId, subscribeToPush]);

  const [notification, setNotification] = useState(null);
  const ws = useRef(null);
  useEffect(() => {
    const token = localStorage.getItem('kor35_token');
    if (!token) return undefined;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}/ws/notifications/?token=${encodeURIComponent(token)}`;
    if (ws.current) ws.current.close();
    ws.current = new WebSocket(wsUrl);
    ws.current.onmessage = (e) => {
      try {
        const d = JSON.parse(e.data);
        const inner = d.type === 'notification' ? d.payload : d;
        const action = inner?.action;
        const payload = inner?.payload || inner;

        if (action === 'MSG_PRIORITARIO') {
          const destId = inner?.destinatario_id != null ? String(inner.destinatario_id) : '';
          if (destId && destId === String(selectedCharacterId || '')) {
            window.dispatchEvent(new CustomEvent('kor35:msg-prioritario', {
              detail: {
                esito_id: inner.esito_id,
                titolo: inner.titolo,
                testo: inner.testo,
              },
            }));
          }
        }

        if (action && String(action).startsWith('VOCE_')) {
          window.dispatchEvent(new CustomEvent('kor35:voce', { detail: inner }));
          // Solo il chiamato (o staff verso_staff) riceve toast OS: il chiamante
          // è già nella stessa room WS e non deve vedere "ti sta chiamando".
          const isCaller =
            selectedCharacterId &&
            String(inner?.chiamante_id) === String(selectedCharacterId);
          if (!isCaller && action === 'VOCE_INVITO') {
            const who = inner?.chiamante_nome || 'Qualcuno';
            sendSystemNotification(
              'Chiamata vocale',
              `${who} ti sta chiamando.`,
              { tag: `voce-${inner?.call_id || 'invito'}`, renotify: true }
            );
          } else if (!isCaller && action === 'VOCE_PERSA') {
            const who = inner?.chiamante_nome || 'Qualcuno';
            sendSystemNotification(
              'Chiamata persa',
              `Hai perso una chiamata da ${who}.`,
              { tag: `voce-persa-${inner?.call_id || 'x'}` }
            );
          }
        }

        if (action === 'TIMER_SYNC' && payload) {
            updateTimerState(payload);
        }

        if (action === 'WATCH_SYNC' && payload?.personaggio_id && String(payload.personaggio_id) === String(selectedCharacterId)) {
            queryClient.invalidateQueries({ queryKey: ['personaggio', String(selectedCharacterId)] });
        }

        if (action === 'TIMER_INNESCO_SYNC' && payload) {
            const ids = payload.recipient_personaggio_ids || [];
            const myId = parseInt(selectedCharacterId, 10);
            const destinatari = ids.map((id) => Number(id));
            if (!destinatari.length || destinatari.includes(myId)) {
              updateTimerState({
                id: payload.id,
                nome: payload.nome,
                istanza: payload.istanza,
                data_fine: payload.data_fine,
                alert_suono: true,
                // Push scadenza: worker server (dispatch_timer_expiry), evita doppia Notification locale
                notifica_push: false,
                messaggio_in_app: true,
                segnale_luminoso: payload.segnale_luminoso !== false,
                source: 'innesco_timer',
              });
            }
        }

        if (action === 'TIMER_TRAPPOLA_SYNC' && payload) {
            const ids = payload.recipient_personaggio_ids || [];
            const myId = parseInt(selectedCharacterId, 10);
            if (!ids.length || ids.includes(myId)) {
              updateTimerState({
                nome: payload.nome,
                data_fine: payload.data_fine,
                alert_suono: payload.alert_suono !== false,
                notifica_push: payload.notifica_push !== false,
                messaggio_in_app: payload.messaggio_in_app !== false,
                variant: 'danger',
              });
            }
        }

        if (action === 'DUELLO_INVITO' && inner?.destinatario_personaggio_id) {
          const myId = parseInt(selectedCharacterId, 10);
          if (String(inner.destinatario_personaggio_id) === String(myId)) {
            const titolo = 'Sfida duello carte';
            const testo = `${inner.sfidante_nome || 'Un avversario'} ti ha sfidato! Apri la tab Carte per accettare la partita.`;
            setNotification({ titolo, testo, tipo: 'INDV' });
            sendSystemNotification(titolo, testo);
          }
        }

        if (
          (action === 'DUELLO_LOBBY' || action === 'DUELLO_PREMATCH' || action === 'DUELLO_INIZIO')
          && inner?.destinatario_personaggio_id
        ) {
          const myId = parseInt(selectedCharacterId, 10);
          if (String(inner.destinatario_personaggio_id) === String(myId)) {
            const titolo = action === 'DUELLO_LOBBY'
              ? 'Scontro carte'
              : action === 'DUELLO_PREMATCH'
                ? 'Pre-partita carte'
                : 'Duello iniziato';
            const testo = action === 'DUELLO_LOBBY'
              ? `${inner.sfidato_nome || 'Un avversario'} si è unito al tuo scontro! Apri la tab Carte.`
              : action === 'DUELLO_PREMATCH'
                ? `${inner.da_nome || 'Il tuo avversario'} ha aggiornato la pre-partita. Apri la tab Carte.`
                : `La partita contro ${inner.avversario_nome || 'l\'avversario'} è iniziata! Apri la tab Carte.`;
            setNotification({ titolo, testo, tipo: 'INDV' });
            sendSystemNotification(titolo, testo);
            window.dispatchEvent(new CustomEvent('kor35:duello-carte', { detail: inner }));
          }
        }

        if (d.type === 'notification') {
           const msg = d.payload;
           if (!msg || msg.action === 'TIMER_SYNC' || msg.action === 'TIMER_INNESCO_SYNC') {
             return;
           }
           if (msg.action && String(msg.action).startsWith('DUELLO_')) {
             return;
           }
           if (msg.action && String(msg.action).startsWith('VOCE_')) {
             return;
           }
           if (msg.action === 'MSG_PRIORITARIO') {
             return;
           }
           const myId = String(selectedCharacterId || '');
           const destId = msg.destinatario_id != null ? String(msg.destinatario_id) : '';
           const forMe =
             msg.tipo === 'BROAD' ||
             msg.tipo === 'GROUP' ||
             msg.tipo === 'STAFF' ||
             (msg.tipo === 'INDV' &&
               ((destId && destId === myId) || !msg.destinatario_id));
           if (forMe) {
              setNotification(msg);
              sendSystemNotification(
                msg.titolo || 'Nuovo messaggio',
                htmlToPlainText(msg.testo),
                { tag: msg.id ? `msg-${msg.id}` : 'msg', renotify: true }
              );
              fetchUserMessages(selectedCharacterId);
              queryClient.invalidateQueries(['personaggio', selectedCharacterId]);
           }
        }
      } catch (err) {}
    };
    return () => { if (ws.current) ws.current.close(); };
  }, [selectedCharacterId, fetchUserMessages, queryClient, updateTimerState]);


  // --- VALUE DEL CONTEXT ---
  const value = useMemo(() => ({
    onLogout,
    personaggiList: personaggiListOrdinata,
    punteggiList,
    statisticaContainers,
    selectedCharacterId,
    preferredCharacterId,
    
    characterData: selectedCharacterData,
    selectedCharacterData,
    
    acquirableSkills,
    acquirableInfusioni,
    acquirableTessiture,
    acquirableCerimoniali,

    activeTimers,
    setActiveTimers,
    updateTimerState,
    removeTimerState,
    refreshActiveTimers,
    
    isLoading: isLoadingList || isLoadingDetail || isLoadingPunteggi || isLoadingStatContainers || mutatingCount > 0,
    isLoadingList,
    isLoadingDetail,
    isSyncing: mutatingCount > 0,
    
    selectCharacter: handleSelectCharacter,
    setPreferredCharacter,
    refreshCharacterData,
    fetchPersonaggi, 
    
    loadSkillsOnDemand: () => {}, 
    loadInfusioniOnDemand: () => {},
    loadTessitureOnDemand: () => {},

    isCampaignMaster,
    isCampaignHeadMaster,
    isCampaignStaffer,
    isCampaignHelper,
    canSeeStaffCompiti,
    isCampaignRedactor,
    activeCampaignRole,
    staffWorkMode,
    setStaffWorkMode,
    campaigns,
    refreshCampaigns,
    activeCampaign,
    changeActiveCampaign,
    /** Superuser Django (bypass globale). Alias legacy per il codice esistente. */
    isGlobalSuperuser,
    isAdmin: isGlobalSuperuser,
    isDjangoStaff,
    canUseWizardTest,
    moduliAccesso,
    canAccessModulo,
    viewAll,
    toggleViewAll,
    adminPendingCount,
    userMessages,
    unreadCount,
    fetchUserMessages,
    handleMarkAsRead,
    handleToggleRead,
    handleDeleteMessage,
    subscribeToPush,
    isWebPushSupported,
    isDevicePushSupported,
    giocoEventoStato,
    eventoAperto: !!giocoEventoStato.evento_aperto,
    azioniLiveAbilitate: !!giocoEventoStato.azioni_live_abilitate,
    bypassEventoGate: !!giocoEventoStato.bypass_evento_gate,
    transazioniGiocatoreAbilitate: !!giocoEventoStato.azioni_live_abilitate,
    nodoScanAbilitato: !!giocoEventoStato.nodo_scan_abilitato,
    allibratoreCodiciAbilitati: !!giocoEventoStato.allibratore_codici_abilitati,
  }), [
    onLogout,
    personaggiListOrdinata,
    punteggiList,
    statisticaContainers,
    selectedCharacterId,
    preferredCharacterId,
    selectedCharacterData,
    acquirableSkills,
    acquirableInfusioni,
    acquirableTessiture,
    acquirableCerimoniali,
    activeTimers,
    updateTimerState,
    removeTimerState,
    refreshActiveTimers,
    isLoadingList,
    isLoadingDetail,
    isLoadingPunteggi,
    isLoadingStatContainers,
    mutatingCount,
    handleSelectCharacter,
    setPreferredCharacter,
    refreshCharacterData,
    fetchPersonaggi,
    isCampaignMaster,
    isCampaignHeadMaster,
    isCampaignStaffer,
    isCampaignHelper,
    canSeeStaffCompiti,
    isCampaignRedactor,
    activeCampaignRole,
    staffWorkMode,
    campaigns,
    refreshCampaigns,
    activeCampaign,
    changeActiveCampaign,
    isGlobalSuperuser,
    isDjangoStaff,
    canUseWizardTest,
    moduliAccesso,
    canAccessModulo,
    viewAll,
    toggleViewAll,
    adminPendingCount,
    userMessages,
    unreadCount,
    fetchUserMessages,
    handleMarkAsRead,
    handleToggleRead,
    handleDeleteMessage,
    subscribeToPush,
    giocoEventoStato,
  ]);

  return (
    <CharacterContext.Provider value={value}>
      {children}
      <NotificationPopup
        notification={notification}
        onClose={() => setNotification(null)}
        onOpenMessages={() => {
          window.dispatchEvent(new CustomEvent('kor35:open-messaggi'));
        }}
      />
      <PriorityMessageOverlay personaggioId={selectedCharacterId} onLogout={onLogout} />
    </CharacterContext.Provider>
  );
};

export const useCharacter = () => {
  const context = useContext(CharacterContext);
  if (!context) throw new Error('useCharacter deve essere usato dentro un CharacterProvider');
  return context;
};